/* eslint-disable consistent-return,@typescript-eslint/camelcase */
/* eslint-disable camelcase */

import { createSelector } from 'reselect';
import * as R from 'ramda';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import { TYPE_LOGOUT, TYPE_LOGOUT_SOFT } from 'constants/Login';
import { REDUX_SETTINGS } from 'constants/persistence';
import { getCafaEntry } from 'reducers/cafaConfigs';
import { getClientCode, getUserLoggedIn, getUserRights } from 'reducers/Login';
import { ErplyAttributes, isTruey } from 'utils/index';
import * as c from 'constants/configs';
import { getProductGroupByID } from 'reducers/productGroupsDB';
import { createOverrideSelector } from 'reducers/Plugins';
import { SO } from 'services/DB/types';
import * as types from 'constants/CAFA';
import { getUseCustomerRegistry } from 'reducers/customerSearch';

import { getProductByID } from '../cachedItems/products';
import { applyRounding } from '../../utils/rounding/rounding';
import { getTotal } from '../Payments';

import { getCurrency, getCurrencyDenominations } from './currency';

export const settingsState = {
  touchpos_out_of_stock_warning: '',
  pos_languages: [
    {
      name: 'Arabic',
      nativeName: 'العَرَبِيَّة',
      shortDisplayName: 'العَرَبِيَّة',
      legacyIdentifier: 'lit',
      isoCode: 'ar',
      code: 'ar',
      countries: ['AE', 'SA'],
      isOverride: true,
    },
    {
      name: 'Czech',
      nativeName: 'Čeština',
      shortDisplayName: 'CS',
      legacyIdentifier: 'est',
      isoCode: 'cs',
      code: 'cs',
      countries: ['CZ'],
      isOverride: true,
    },
    {
      name: 'Danish',
      nativeName: 'Dansk',
      shortDisplayName: 'DA',
      legacyIdentifier: 'spa',
      isoCode: 'da',
      code: 'da',
      countries: ['DK', 'FO'],
      isOverride: true,
    },
    {
      name: 'Dutch',
      nativeName: 'Nederlands',
      shortDisplayName: 'NL',
      legacyIdentifier: 'gre',
      isoCode: 'nl',
      code: 'nl',
      countries: ['NL', 'BE'],
      isOverride: true,
    },
    {
      name: 'English',
      nativeName: 'English',
      shortDisplayName: 'ENG',
      legacyIdentifier: 'eng',
      isoCode: 'en',
      countries: [],
      isOverride: false,
    },
    {
      name: 'English (Australia)',
      nativeName: 'English',
      shortDisplayName: 'ENG',
      legacyIdentifier: 'eng',
      isoCode: 'en',
      code: 'en-AU',
      countries: ['AU'],
      isOverride: false,
    },
    {
      name: 'Estonian',
      nativeName: 'Eesti keel',
      shortDisplayName: 'EST',
      legacyIdentifier: 'est',
      isoCode: 'et',
      countries: [],
      isOverride: false,
    },
    {
      name: 'French',
      nativeName: 'Français',
      shortDisplayName: 'FRE',
      legacyIdentifier: 'fre',
      isoCode: 'fr',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Faroese',
      nativeName: 'Føroyskt',
      shortDisplayName: 'FO',
      legacyIdentifier: 'lit',
      isoCode: 'fo',
      countries: ['DK', 'FO'],
      isOverride: true,
    },
    {
      name: 'Finnish',
      nativeName: 'Suomen kieli',
      shortDisplayName: 'FIN',
      legacyIdentifier: 'fin',
      isoCode: 'fi',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Spanish',
      nativeName: 'Español',
      shortDisplayName: 'SPA',
      legacyIdentifier: 'spa',
      isoCode: 'es',
      countries: [],
      isOverride: false,
    },
    {
      name: 'German',
      nativeName: 'Deutsch',
      shortDisplayName: 'GER',
      legacyIdentifier: 'ger',
      isoCode: 'de',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Russian',
      nativeName: 'Русский язык',
      shortDisplayName: 'RUS',
      legacyIdentifier: 'rus',
      isoCode: 'ru',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Swedish',
      nativeName: 'Svenska',
      shortDisplayName: 'SWE',
      legacyIdentifier: 'swe',
      isoCode: 'sv',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Latvian',
      nativeName: 'Latviešu',
      shortDisplayName: 'LAT',
      legacyIdentifier: 'lat',
      isoCode: 'lv',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Lithuanian',
      nativeName: 'Lietuvių kalba',
      shortDisplayName: 'LIT',
      legacyIdentifier: 'lit',
      isoCode: 'lt',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Greek',
      nativeName: 'Ελληνικά',
      shortDisplayName: 'GRE',
      legacyIdentifier: 'gre',
      isoCode: 'el',
      countries: [],
      isOverride: false,
    },
    {
      name: 'Italian',
      nativeName: 'Italiano',
      shortDisplayName: 'IT',
      legacyIdentifier: 'lat',
      isoCode: 'it',
      countries: ['IT'],
      isOverride: true,
    },
    {
      name: 'Polish',
      nativeName: 'Polski',
      shortDisplayName: 'PL',
      legacyIdentifier: 'gre',
      isoCode: 'pl',
      countries: ['PL'],
      isOverride: true,
    },
    {
      name: 'Thai',
      nativeName: 'ภาษาไทย',
      shortDisplayName: 'TH',
      legacyIdentifier: 'fin',
      isoCode: 'th',
      countries: ['TH'],
      isOverride: true,
    },
    {
      name: 'Turkish',
      nativeName: 'Türkçe',
      shortDisplayName: 'TR',
      legacyIdentifier: 'lit',
      isoCode: 'tr',
      countries: ['TR'],
      isOverride: true,
    },
    {
      name: 'Vietnamese',
      nativeName: 'Tiếng Việt',
      shortDisplayName: 'VN',
      legacyIdentifier: 'lat',
      isoCode: 'vi',
      countries: ['VN'],
      isOverride: true,
    },
    {
      name: 'Norwegian',
      nativeName: 'Norsk',
      shortDisplayName: 'NO',
      legacyIdentifier: 'gre',
      isoCode: 'no',
      countries: ['NO', 'SE', 'FI', 'DK', 'FO', 'EE', 'IS'],
      isOverride: true,
    },
  ],
};

export const settings = (state = settingsState, { type, data }) => {
  switch (type) {
    case c.SAVE_SETTING_LOCALSTORAGE:
      localStorageCanary += 1;
      if (data.value === undefined) {
        localStorage.removeItem(data.key);
      } else {
        localStorage.setItem(data.key, data.value);
      }
    case c.SET_SETTINGS_SUCCESS:
      return { ...state, ...data };
    case c.SAVE_SETTING_SUCCESS:
      return { ...state, ...data };
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return settingsState;
    default:
      return state;
  }
};

export const objects = (state = {}, { type, data }) => {
  switch (type) {
    case c.SET_OBJECTS.SUCCESS:
      return { ...state, ...data };

    default:
      return state;
  }
};

export const tempSettings = (state = {}, { type, payload }) => {
  switch (type) {
    case c.TEMP_SETTINGS_SET:
      return { ...state, ...payload };
    case c.TEMP_SETTINGS_DROP:
      return R.omit(payload, state);
    default:
      return state;
  }
};

export const getCurrencyId = state =>
  state.configs.currency.default?.currencyID;

export const getCurrencyCode = state =>
  state.configs.currency.default?.code ?? '';

/** @return {{name?: string, value: number}[]} */
export const getPosCurrencyUnits = state => {
  const units = getSetting(
    'pos_currency_units',
    '',
  )(state)
    ?.split(',')
    .map(e => e?.trim())
    .filter(a => a.length)
    .map(pair => {
      const [a, b] = pair.split('=');
      if (b) {
        return { name: a, value: Number(b) };
      }
      return { value: Number(pair) };
    });
  return units.length ? units : null;
};

/**
 * @callback innerSelector
 * @param {import('../../reducers').RootState} state
 * @returns {import('../../containers/Forms/OpenCloseDay/OCDTypes').Denominations}
 *
 * @param {string} code
 * @returns {innerSelector}
 */
export const getDenominations = code => state => {
  return getCurrencyDenominations(code)(state);
};

export const getDiscountPercentages = createSelector(
  state => getSetting('touchpos_custom_discount_percentages', '')(state),
  conf => {
    const customPercentages = `${conf}`
      .split(',')
      .map(a => Number(a))
      .filter(a => a);
    const defaultPercentages = [0, 5, 10, 15, 20, 25, 50];
    const percentages =
      customPercentages.length > 0 ? customPercentages : defaultPercentages;
    return R.uniq([0, ...percentages]);
  },
);

export const getFallbackLanguagesFor = type => state => {
  const defaultOrderOfFallbackLangs =
    type === 'receipt'
      ? 'en,et,ru,fr,es,de,fi,cs,it,tr'
      : 'eng,est,rus,fre,spa,ger,fin,ces,ita,tur';
  const backOfficeLang = getPosDefaultLanguage(state)[
    type === 'receipt' ? 'isoCode' : 'legacyIdentifier'
  ];
  // If you add languages for receipts, don't forget to update supportedLangs with language
  // you're adding
  const supportedLangs =
    type === 'receipt'
      ? ['en', 'et', 'ru', 'fr', 'es', 'de', 'fi', 'cs', 'it', 'tr']
      : ['eng', 'est', 'rus', 'fre', 'spa', 'ger', 'fin', 'ces', 'ita', 'tur'];
  const dataFromConf = getSetting(
    'touchpos_set_printing_language_priority',
    defaultOrderOfFallbackLangs,
  )(state)?.trim();
  const splitDataFromConf = dataFromConf
    ? dataFromConf
        ?.split(',')
        ?.map(el => el.trim())
        .concat(supportedLangs)
    : supportedLangs;
  return [backOfficeLang, ...splitDataFromConf].filter(value =>
    supportedLangs.includes(value),
  );
};

let localStorageCanary = 1;
const getSettingsInLocalstorage = createSelector(
  () => localStorageCanary,
  state => getClientCode(state),
  (_, clientCode) =>
    Object.assign(
      {},
      ...Object.keys(localStorage)
        .map(key => key.match(/^CONFIGURATION: (\d+) - (.+)/))
        .filter(match => match)
        .filter(([, cc]) => cc === clientCode)
        .map(([whole, , setting]) => ({
          [`CONFIGURATION: ${setting}`]: JSON.parse(
            localStorage.getItem(whole),
          ),
        })),
    ),
);

const getStoredSettings = state => {
  try {
    return JSON.parse(localStorage.getItem(REDUX_SETTINGS));
  } catch (e) {
    return null;
  }
};

const getModules = createSelector(
  state => getSettings(state),
  settings => settings?.additionalModules ?? [],
);

export const getIsModuleEnabled = name =>
  createSelector(
    getModules,
    modules => modules.find(m => m.name === name)?.enabled === 1,
  );

export const getSavedSettings = createSelector(
  state => getSettingsInLocalstorage(state),
  state => state.configs?.settings,
  state => getStoredSettings(state),
  (localStorageSettings, settings, storedSettings) => {
    if (Object.values(settings || {}).length > 2) {
      localStorage.setItem(
        REDUX_SETTINGS,
        JSON.stringify({
          ...localStorageSettings,
          ...settings,
        }),
      );
    } else if (storedSettings) {
      return storedSettings;
    }

    return {
      ...localStorageSettings,
      ...settings,
    };
  },
);
const getSettingsBase = createSelector(
  () => {
    try {
      return (
        JSON.parse(sessionStorage.getItem('debug: settingsOverride')) ?? {}
      );
    } catch (e) {
      return {};
    }
  },
  state => getSavedSettings(state),
  state => getTempSettings(state),
  (ovr, saved, temp) =>
    R.reduce(R.mergeDeepWith((a, b) => a ?? b))({})([ovr, temp, saved]),
);
export const getSettings = createOverrideSelector(
  'getSettings',
  getSettingsBase,
);

export const getSetting = (name, defaultValue) => state => {
  const settings = getSettings(state);
  const key = Object.keys(settings).find(
    key => key.toLowerCase() === name.toLowerCase(),
  );
  // If we don't check for this explicitly, the code will read `settings[undefined]` which might just happen to have a value
  // This value is unlikely to be valid for all missing settings, and thus is likely to cause crashes
  if (key === undefined) {
    return defaultValue;
  }
  return settings[key] ?? defaultValue;
};

export const getSavedSetting = (settingName, defaultValue) =>
  createSelector(getSavedSettings, settings => {
    const key = Object.keys(settings).find(
      key => key.toLowerCase() === settingName.toLowerCase(),
    );
    return settings[key] ?? defaultValue;
  });

const UNDEFINED = Symbol('UNDEFINED');
const getSettingIsUndefined = name => state =>
  getSetting(name, UNDEFINED)(state) === UNDEFINED;
/**
 * True if it is allowed to have positive quantities on return documents, false otherwise
 * @default 1
 */
export const getIsExchangeAllowed = getSetting(
  'allow_exchange_products_on_return',
  1,
);

export const getPriceEditingAllowedForExchangesAndReturns = getSetting(
  'allow_price_editing_for_exchanges_and_returns',
  0,
);

export const getWarehouseSelect = state =>
  getSetting('touchpos_warehouse_select', 0)(state) === 1;

export const getCheckBehavior = state => {
  if (Number(getSetting('capture_check_numbers')(state)) === 1) {
    return 'NEW';
  }
  if (Number(getSetting('touchpos_enable_check_extra_fields')(state)) === 1) {
    return 'OLD';
  }
  return 'NOT_CONFIGURED';
};

export const getAllRegisters = state =>
  getSetting('touchpos_show_all_registers', 0)(state) === 1;

export const getDisableProductAddedNotification = state =>
  getSetting('touchpos_disable_product_added_notification', 0)(state) === 1;

export const getSortProductsBy = getSetting(
  'touchpos_sort_products_by',
  'code',
);

export const getIsNonDiscountableProductsEnabled = state =>
  getSetting('touchpos_non_discountable_products', 0)(state) === 1;

export const getIsCustomPaymentEnabled = state =>
  getSetting('touchpos_enable_custom_payments', 0)(state) === 1;
export const getShouldShowTaxRateInsteadOfName = state =>
  getSetting('show_tax_rate_instead_of_name', 0)(state) === 1;
export const getOrderProductsByInProductGroup = state =>
  getSetting('touchpos_order_products_by_in_product_group', 'changed')(state);
export const getOrderProductsDirectionInProductGroup = state =>
  getSetting(
    'touchpos_order_products_direction_in_product_group',
    'asc',
  )(state);

export const getPosRoundCash = createSelector(
  state => getSetting('pos_round_cash_to_nearest_x')(state),
  newSetting => {
    return parseInt(newSetting || '0', 10) / 100;
  },
);

export const getRoundingDirection = createSelector(
  state => getSetting('pos_round_direction'),
  dir => {
    try {
      /** type [unknown, 'true'|'false', '+inf'| '-inf'| 'inf'| '0'| 'even'| 'odd'] */
      const [, whole, target] = dir.match(/(.+?),(.+?),(.+?)/);
      return value => {
        const fraction = value % 1;
        // If not whole rounding, then round to nearest
        if (Math.abs(fraction) !== 0.5) {
          return Math.round(value);
        }
        // If there is no nearest, or we're rounding whole, then do directional
        switch (dir) {
          case 'even':
            return 2 * Math.round(value / 2);
          case 'odd':
            return 2 * Math.round((value + 1) / 2);
          case '0':
            return Math.trunc(value);
          case 'inf':
            return Math.trunc(value) + Math.sign(value);
          case '+inf':
            return Math.ceil(value);
          case '-inf':
            return Math.floor(value);
        }
      };
    } catch (e) {
      return Math.round;
    }
  },
);

export const getPosRoundCashFunction = createSelector(
  state => getPosRoundCash(state),
  state => getSetting('pos_round_algo')(state),
  state => getTotal(state),
  (roundingAmount, algoConf, total) => (value, options) => {
    if (roundingAmount === 0) {
      return value;
    }
    let [whole, target] = ['false', 'inf'];
    try {
      [, whole, target] = algoConf.match(/^(.+?),(.+?)$/);
    } catch (e) {
      console.warn(
        'Failed to parse rounding algorithm, falling back to default',
        e,
      );
    }
    return (
      applyRounding(Number(value) / roundingAmount, {
        wholeRounding: options?.wholeRounding ?? whole === 'true',
        type: options?.type ?? 'payment',
        algorithm: options?.algorithm ?? target,
        direction: options?.direction ?? total < 0 ? 'refund' : 'sale',
      }) * roundingAmount
    );
  },
);

/**
 * Return configured serial number length. 0 if turned off, otherwise string value
 * @param {*} state
 * @returns { string | 0 }
 */
export const getSerialNumberLength = state =>
  getSetting('serial_number_length', '1')(state);

export const getSerialNumberOperator = getSetting(
  'serial_number_operator',
  'minLength',
);

export const getSerialNumberTitle = getSetting('serial_name_title', 'SN');

/**
 * @returns {{
 *  hideCustomerInfo: 0 | 1,
 *  hideCustomerSearch: 0 | 1,
 *  hideAddProductOrGroup: 0 | 1,
 *  hideAddCustomer: 0 | 1,
 *  hideRecentSales: 0 | 1,
 *  hidePendingSales: 0 | 1,
 *  hideOrders: 0 | 1,
 *  hideLayaways: 0 | 1,
 *  hideCloseDay: 0 | 1,
 *  hideXReport: 0 | 1,
 *  hideCashInOut: 0 | 1,
 *  hidePriceLookup: 0 | 1,
 *  hideClockInOut: 0 | 1,
 *  hideGiftCardBalance: 0 | 1,
 *  hidePrintCoupons: 0 | 1,
 *  hideNewSale: 0 | 1,
 *  hideSaveSale: 0 | 1,
 *  hideDiscount: 0 | 1,
 *  hideTaxExcempt: 0 | 1,
 *  hideNotes: 0 | 1,
 *  hideLastReceiptPrint: 0 | 1,
 *  hideOpenCashDrawer: 0 | 1,
 *  hidePromotions: 0 | 1,
 *  hideCoupons: 0 | 1,
 *  hidePrintGiftcardReceipt: 0 | 1,
 *  hideAddShipping: 0 | 1,
 *  hideSwitchUser: 0 | 1,
 *  isCustomerHomeStoreDisabled: 0 | 1,
 * }}
 */
export const getUISettings = createSelector(
  state => getSetting('touchpos_ui_configuration', '{}')(state),
  str => {
    try {
      const res = JSON.parse(str);
      return res ?? {};
    } catch (e) {
      return {};
    }
  },
);

const defaultCustomerSearchResultsConfiguration = {
  name: 1,
  email: 1,
  retail_name: 1,
  retail_email: 1,
  retail_phone: 1,
  retail_cardNr: 1,
};

export const getCustomerSearchResultsConfiguration = createSelector(
  state =>
    getSetting(
      'customer_search_results_configuration',
      // Default configuration
      '{"name": 1, "email": 1,"retail_name": 1,"retail_email":1,"retail_phone":1, "retail_cardNr":1 }',
    )(state),
  str => {
    try {
      const res = JSON.parse(str);
      return res ?? defaultCustomerSearchResultsConfiguration;
    } catch (e) {
      return defaultCustomerSearchResultsConfiguration;
    }
  },
);

const defaultCustomerSearchResultsSortingConfiguration = {
  direction: 'desc',
  criteria: 'name',
  enabled: false,
};

/**
 * @param {import('reducers').RootState} state
 * @returns {(import('./types').CustomerSortingConf)}
 */
export const getCustomerSearchResultsSortingConfiguration = state => {
  const str = getSetting(
    'customer_search_results_sorting_configuration',
    '',
  )(state);
  const usingCustomerRegistry = getUseCustomerRegistry(state);
  try {
    const res = JSON.parse(str);
    if (res) {
      return { ...res, enabled: usingCustomerRegistry ? false : !!res.enabled };
    }
    throw new Error('No customer_search_results_sorting_configuration config');
  } catch (e) {
    return {
      ...defaultCustomerSearchResultsSortingConfiguration,
      enabled: usingCustomerRegistry
        ? false
        : defaultCustomerSearchResultsSortingConfiguration.enabled,
    };
  }
};

/** export
 * @param {"hideCustomerInfo" | "hideCustomerSearch" | "hideAddProductOrGroup" | "hideAddCustomer" | "hideRecentSales" | "hidePendingSales" | "hideOrders" | "hideLayaways" | "hideCloseDay" | "hideXReport" | "hideCashInOut" | "hidePriceLookup" | "hideClockInOut" | "hideGiftCardBalance" | "hidePrintCoupons" | "hideNewSale" | "hideSaveSale" | "hideDiscount" | "hideTaxExcempt" | "hideNotes" | "hideLastReceiptPrint" | "hideOpenCashDrawer" | "hidePromotions" | "hideCoupons" | "hidePrintGiftcardReceipt" | "hideAddShipping" | "hideSwitchUser" | "isCustomerHomeStoreDisabled" | "isFullScreen" | "isEvenColumns" | "hideProductCommission" | "touchpos_app_background_url" | "touchposPaymentButtonsHighlight" } name
 * @returns {function (state) : (0|1|string)}
 */
export const getUISetting = name => state => getUISettings(state)[name];

/**
 * @returns {'code' | 'barcode'}
 */
export const getProductCodeToShowOnReturnModal = createSelector(
  getSetting('productCodeToShowOnReturnModal'),
  conf => conf ?? 'code',
);

export const getPosLanguages = state => state.configs.settings.pos_languages;
export const getPosDefaultLanguage = state =>
  state.configs.settings.pos_default_language ?? {};
export const getBackOfficeDefaultLanguage = state =>
  state.configs.settings.default_language;
export const getBackOfficeFooter = state =>
  state.configs.settings.receipt_footer;
export const getEmployeeIdentifierOnReceipt = state =>
  state.configs.settings.employee_identifier_on_receipt;
export const getBOCodeOnReceipt = state =>
  state.configs.settings.code_on_receipt;
export const getBOtotalDiscountLabel = state =>
  state.configs.settings.total_discount_label;

export const getCountryInUse = state => state.configs.settings.country;

/**
 * @returns {string} symbol
 */
export const getCurrencySymbol = state =>
  getCurrency(getCurrencyCode(state))(state).symbol;

export const getCurrencyFormatter = state =>
  getCurrency(getCurrencyCode(state))(state).format;

export const getCurrencyFormatterForCurrency = currency =>
  createSelector(getCurrency(currency), currency => currency.format);

/**
 - JSON - appending an item to a stringified list
 const value = JSON.parse(str)
 const newStr = JSON.stringify(value.concat(newItem))
 - JSON - setting a property on a stringified object
 const value = JSON.parse(str)
 value.enabled = false;
 const newStr = JSON.stringify(value)
 - CURR - multiplying a stringified sum by a rate
 const value = CURR.parse(str)
 const newStr = CURR.stringify(value * rate)
 */
export const getCurrencyFormatterNoSymbol = createSelector(
  state => getCurrency(getCurrencyCode(state))(state),
  currency => ({
    stringify: currency.formatNoSymbol,
    parse: currency.unformatNoSymbol,
  }),
);

export const getCurrencyWholeFormatter = state =>
  getCurrency(getCurrencyCode(state))(state).wholeFormat;
export const getCurrencyCentsFormatter = state =>
  getCurrency(getCurrencyCode(state))(state).centsFormat;

export const posSupportedLangsDict = {
  ar: 'lit',
  cs: 'est',
  en: 'eng',
  'en-ZA': 'eng',
  'en-AU': 'eng',
  'en-GB': 'eng',
  et: 'est',
  fi: 'fin',
  de: 'ger',
  it: 'lat',
  es: 'spa',
  fr: 'lat',
};

// Update this array in case any language is not returned via getActiveLanguages
export const missingLanguages = [
  {
    code: 'en-ZA',
    countries: ['ZA'],
    isOverride: false,
    isoCode: 'en',
    legacyIdentifier: 'eng',
    name: 'English (South Africa)',
    nativeName: 'English',
    shortDisplayName: 'ENG',
  },
  {
    code: 'en-GB',
    countries: [],
    isOverride: false,
    isoCode: 'en-gb',
    legacyIdentifier: 'eng',
    name: 'English (GB)',
    nativeName: 'English',
    shortDisplayName: 'ENG',
  },
];

export const getPosActiveLanguage = state => {
  const {
    use_default_language: useDefault,
    'CONFIGURATION: lang': currLang,
    pos_default_language: posLang,
  } = getSettings(state);

  return isTruey(useDefault) ? posLang?.isoCode : currLang;
};

export const getBackOfficeInvoiceAnouncement = state => {
  return getFallbackLanguagesFor('announcement')(state)
    .map(lang => getSetting(`invoice_announcement_${lang}`)(state))
    .find(a => a);
};

export const getTempSettings = state => state.configs.tempSettings;

const getLimitFor = (docType, pmtType) =>
  createSelector(
    getSetting(`pos_allow_${docType}_${pmtType}`),
    getSetting(`pos_allow_${docType}_${pmtType}_limit`),
    (allow, amount) => {
      // Since it is not possible to return tips whatsoever when making a referenced or unreferenced return, allow value should be set to -1
      if (pmtType === 'tip') {
        if (docType === 'return' || docType === 'return_receipt') {
          return -1;
        }
      }
      if (!allow) {
        return -1;
      }
      if (!amount) {
        return null;
      }
      if (amount === '') {
        return null;
      }
      if (Number.isNaN(amount)) {
        return null;
      }
      if (Number(amount) <= 0) {
        return null;
      }
      return Number(amount);
    },
  );

const getLimitForGiftcard = docType =>
  createSelector(
    getLimitFor(docType, 'giftcard_regular'),
    getSetting('pos_use_giftcards_with_serial_numbers'),
    getSettingIsUndefined(`pos_allow_${docType}_giftcard_regular`),
    (limit, defaultOff, unconfigured) => {
      if (defaultOff && unconfigured) {
        return -1;
      }
      return limit;
    },
  );

export const getLimitsFor = docType =>
  createSelector(
    getLimitFor(docType, 'cash'),
    getLimitFor(docType, 'card'),
    getLimitForGiftcard(docType),
    getLimitFor(docType, 'giftcard_serial'),
    getLimitFor(docType, 'storecredit'),
    getLimitFor(docType, 'check'),
    getLimitFor(docType, 'tip'),
    getLimitFor(docType, 'transfer'),
    (
      cash,
      card,
      giftcard,
      giftcardSerial,
      storeCredit,
      check,
      tip,
      transfer,
    ) => [
      { type: 'CASH', amount: cash },
      { type: 'CARD', amount: card },
      { type: 'GIFTCARD', amount: giftcard },
      { type: 'GIFTCARD', serial: true, amount: giftcardSerial },
      { type: 'STORECREDIT', amount: storeCredit },
      { type: 'CHECK', amount: check },
      { type: 'TIP', amount: tip },
      { type: 'TRANSFER', amount: transfer },
    ],
  );

export const getShowPricesWithTax = state =>
  getSetting('locale_uses_price_with_tax')(state) === 1;

export const getShowPricesWithQuantity = state =>
  getSetting('pos_show_prices_in_cart_with_quantity')(state) === 1;

export const getUseLocalProductDB = state =>
  getSetting('brazil_use_local_product_db', 1)(state) === 1;

export const getDisabledDatabaseSO = createSelector(
  state => getUseLocalProductDB(state),
  useLocalProductsDB => [
    SO.CUSTOMERS.NAME,
    /*
    Add new conditions here if more SO should be synced conditionally
   */
    ...(!useLocalProductsDB ? [SO.PRODUCTS.NAME] : []),
  ],
);

export const getSkipSyncForSO = SO_NAME => state =>
  getDisabledDatabaseSO(state).includes(SO_NAME);

export const getFetchOnlyProductsThatAreInStock = state =>
  getSavedSetting('touchpos_fetch_only_products_in_stock', 0)(state) === 1 ??
  false;

export const getUseAgeVerification = state =>
  getSetting('touchpos_use_age_verification', 0)(state) === 1;

/**
 * @returns {{id: string, name: string, SP:string, charset:number}[]}
 */
export const getSupportedPrinterIntegrations = state => [
  { id: '0519 0001', name: 'Star', SP: '\x7F', charset: 32 },
  { id: '04b8 0e15', name: 'Epson', SP: '\xA0', charset: 16 },
  { id: '1504 0059', name: 'Bixolon', SP: '\xA0', charset: 16 },
];

/**
 * @returns {{id: string, name: string, SP:string, charset:number}}
 */
export const getCurrentPrinterIntegration = state => {
  const integrations = getSupportedPrinterIntegrations(state);
  const selected = getSetting('currentPrinterIntegration')(state);
  return integrations.find(int => int.name === selected) || integrations[0];
};

export const getCurrentPosLanguage = createSelector(
  state => getTempSettings(state)?.['CONFIGURATION: lang'],
  state =>
    getCafaEntry(
      types.INTEGRATIONS.posConfigurations.language,
      types.INTEGRATION_TYPES.posConfigurations,
      { level: types.CAFA_LEVELS.User, id: getUserLoggedIn(state)?.userID },
    )(state),
  state => getSetting('CONFIGURATION: lang')(state),
  state => getSetting('pos_default_language')(state),
  (fromTempSettings, fromCAFAConfig, fromLS, defaultLangObj) =>
    fromTempSettings ??
    fromCAFAConfig?.value ??
    (fromLS?.length ? fromLS : undefined) ??
    defaultLangObj?.isoCode,
);

export const getCurrentPosLanguageOverride = createSelector(
  state => getTempSettings(state)['CONFIGURATION: lang'],
  state =>
    getCafaEntry(
      types.INTEGRATIONS.posConfigurations.language,
      types.INTEGRATION_TYPES.posConfigurations,
      { level: types.CAFA_LEVELS.User, id: getUserLoggedIn(state)?.userID },
    )(state),
  state => getSetting('CONFIGURATION: lang')(state),
  (fromTempSettings, fromCAFAConfig, fromLS) =>
    fromTempSettings ?? fromCAFAConfig?.value ?? fromLS,
);

export const getWrapperConfig = createSelector(
  getCafaEntry('wrapper', types.INTEGRATION_TYPES.payment),
  ({ value } = {}) => value,
);

export const getUseCayanCapture = createSelector(
  getSetting('use_cayan_capture'),
  /* Temporarily disabled as per https://erply.atlassian.net/browse/PBIB-1750?focusedCommentId=187006 */
  setting => false,
  // setting => setting === 1,
);

export const getAdditionalCurrencies = createSelector(
  state => getSetting('touchpos_sale_additional_currencies')(state),
  currencies => {
    const codes = currencies
      ?.split(',')
      .map(c => c.trim())
      .filter(c => c.match('^[A-Z]{3}$'));
    return codes ?? [];
  },
);

export const getDefaultCurrency = state =>
  getSetting('default_currency')(state) ??
  state.configs.currency?.default?.code;

export const getEnabledCurrencies = createSelector(
  getIsModuleEnabled('pos_multicurrency'),
  getDefaultCurrency,
  getAdditionalCurrencies,
  (useMultiCurrency, defaultCurrency, additional) =>
    useMultiCurrency
      ? Array.from(new Set([defaultCurrency, ...additional]))
      : [defaultCurrency],
);

export const getShouldShowPrintAttemptCount = state =>
  !!Number(getSetting('touchpos_show_print_attempt_number')(state));

export const getShouldShowCardDataOnReceipt = state =>
  !!Number(getSetting('touchpos_show_card_data_on_receipt')(state));

export const getShouldShowCustomerStoreCreditBalanceOnReceipt = state =>
  !!Number(getSetting('show_customer_store_credit_balance_on_receipt')(state));

export const getShouldChooseEmployeeManually = state =>
  !!Number(getSetting('touchpos_choose_employee_manually')(state));

export const getShouldShowProductCode = state =>
  getSetting('touchpos_show_product_code_on_receipt')(state) === 1;

export const getShouldShowCashChangeOnReceipt = state =>
  getSetting('touchpos_show_change_on_receipt')(state) === 1;

export const getShowTotalDiscountOnReceipt = state =>
  getSetting('touchpos_show_discount_total')(state) === 1;

export const getShowCompanyRegVatNumberOnReceipt = state =>
  !!getSetting('touchpos_show_company_reg_vat_number', 1)(state);

export const getShouldHideNegativeDiscountsOnReceipt = state =>
  getSetting('hide_negative_discounts')(state) === 1;

export const getAllowEditingExistingLayaways = state =>
  getSetting('touchpos_allow_editing_existing_layaways')(state);

export const getDiscountLimit = createSelector(
  state => getUserRights(state),
  rights => {
    const { rightMakeDiscountInPOS } = rights;
    // If user cannot assign discounts, set the limit to 0, else check maxDiscount
    if (rightMakeDiscountInPOS === 0) {
      return 0;
    }
    const md = rights.maxDiscount;
    if (md < 0) {
      return 0;
    }
    if (md === 0) {
      return Number.POSITIVE_INFINITY;
    }
    return md;
  },
);

// Previously known as getIsCashBackDisabled, now Cash Back renamed to Cash Withdrawal/Out
export const getIsCashWithdrawalDisabled = state =>
  // usage of 'disableCashBack' intentional!
  getSetting('disableCashBack', 0)(state);

/**
 * @returns {Selector<string[]>}
 */
export const getCustomPromptsForProduct = id => state => {
  const product = getProductByID(id)(state) ?? {};
  const group = getProductGroupByID(product.groupID)(state);
  const productAttrs =
    new ErplyAttributes(product.attributes).get('custom_prompts') ?? '';
  const groupAttrs =
    new ErplyAttributes(group.attributes).get('custom_prompts') ?? '';

  const asArray = `${productAttrs},${groupAttrs}`.split(',').filter(a => a);
  return Array.from(new Set(asArray)).sort((a, b) => a.localeCompare(b));
};

export const getDayCountedByDrawer = createSelector(
  getSetting('pos_shifts_counted'),
  setting => setting === 'BY_DRAWER',
);

export const getTransactionsOnlyFromLastShift = createSelector(
  getSetting('touchpos_get_report_only_last_shift'),
  setting => Number(setting) === 1,
);
const dateFormatMapping = {
  'd/m/Y': 'DD/MM/YYYY',
  'd.m.Y': 'DD.MM.YYYY',
  'd.m.Y.': 'DD.MM.YYYY.',
  'd-m-Y': 'DD-MM-YYYY',
  'm/d/Y': 'MM/DD/YYYY',
  'Y-m-d': 'YYYY-MM-DD',
  'Y/m/d': 'YYYY/MM/DD',
  'Y.m.d.': 'YYYY.MM.DD.',
  'Y.m.d': 'YYYY.MM.DD',
};
export const dateFormatFromBO = createSelector(
  state => getSetting('dateformat')(state),
  boFormat =>
    (dateFormatMapping[boFormat] ?? 'DD-MM-YYYY')
      .toLowerCase()
      .replace('mm', 'MM'),
);
export const getDateFormatFromBOKeysOrdered = createSelector(
  state => getSetting('dateformat')(state),
  boFormat =>
    (boFormat || 'dmy')
      .toLowerCase()
      .replace(/[^a-z']/g, '')
      .split('')
      .map(el => {
        if (el === 'd') {
          return { key: 'date' };
        }
        if (el === 'm') {
          return { key: 'month' };
        }
        return { key: 'year' };
      }),
);

export const getAPIDateFormatter = createSelector(() => (date, format) =>
  date ? String(dayjs(date, format).format('YYYY-MM-DD')) : '',
);

export const getDateFormatter = createSelector(
  state => getSetting('dateformat', 'd-m-Y')(state),
  boFormat => date =>
    date
      ? dayjs(
          date,
          dateFormatMapping[boFormat]?.toLowerCase()?.replace('mm', 'MM'),
        ).format(dateFormatMapping[boFormat] ?? 'DD-MM-YYYY')
      : date,
);

export const getAllowOfflineLogin = state =>
  !!getSetting('touchpos_allow_offline_login', false)(state);

export const getAllowOfflineMode = state =>
  getSetting('touchpos_allow_offline_mode')(state);

export const getBarcodeType = state => getSetting('barcode_type')(state) ?? '';
export const getBarcodeWeightPrefix = state =>
  getSetting('barcode_weight_prefix')(state) ?? '';
export const getBarcodePricePrefix = state =>
  getSetting('barcode_price_prefix')(state) ?? '';
export const getBarcodeCodeLength = state =>
  Number(getSetting('barcode_code_length')(state)) ?? 0;
export const getBarcodeMeasurementLength = state =>
  Number(getSetting('barcode_measurement_length')(state)) ?? 0;
export const getBarcodeWeightDecimals = state =>
  Number(getSetting('barcode_weight_decimals')(state)) ?? 0;

/**
 * Whether the payment integration's batch should be synced to day openings
 * (batch closed when day closed, batch opened when day opened)
 *
 * @default true
 */
export const getSyncBatchToDayOpenings = createSelector(
  getSetting('sync_terminal_batch_to_day_openings'),
  setting => Number(setting ?? '1'),
);

/**
 * Should Product button expand to show longer product names and under what circumstances
 * @type {OutputSelector<unknown, unknown, (res: unknown) => unknown>}
 */
export const getExpandProductButtonConfig = state =>
  getSetting('pos_expand_product_button', 'disabled')(state);

export const getPrefillHomeStoreOnCustomerCreation = state =>
  !!getSetting(
    'touchpos_prefill_home_store_during_customer_creation',
    false,
  )(state);

/**
 * Should customers be filtered in Customer search input component
 * @returns {"disabled" | "byHomeStore" | "bySignupStore"}
 */
export const getCustomerSearchFilterOption = state =>
  getSetting('pos_customer_search_filter', 'disabled')(state);

export const getSearchCustomerCodeFromMiddle = state =>
  getSetting('pos_search_customer_code_from_middle', true)(state);

export const getProductNoteButtonsConfigs = createSelector(
  state => getSetting('pos_product_quick_notes', '')(state),
  state => getSetting('pos_product_quick_notes_preview_count', 6)(state),
  (notes, previewCount) => {
    if (!notes.trim().length) {
      return { notes: [], previewCount };
    }
    return { notes: notes.split('|noteEnd|'), previewCount };
  },
);

export const getUseCreateCustomerBeta = state =>
  getSetting('pos_brazil_use_customer_creation_beta', false)(state);

export const getProductFilterModuleEnabled = state => {
  return false;
  // Temporary disable the feature cause it is not currently supported
  // return  getSetting('touchpos_filter_products_button', false)(state);
};

export const getSkipCalculateShoppingCartOnChange = state =>
  getSetting(
    'pos_brazil_do_not_calculate_shopping_cart_on_change',
    false,
  )(state);

export const getAllowScanningFromOrderQtyInput = state =>
  getSetting(
    'pos_brazil_allow_scanning_products_from_order_qty_field',
    false,
  )(state);

export const getShouldShowCurrentDocumentTypeAboveCart = state =>
  !!getSetting(
    'pos_brazil_show_current_document_type_above_cart',
    false,
  )(state);
/**
 * Check if POS Version Control is enabled
 * <br/>
 * <em>Initially the implementation is optional and can be toggled with setting. This setting would perhaps be deprecated in the future.</em>
 */
export const getEnabledPOSVersionControl = state =>
  getSavedSetting('pos_brazil_enabled_version_control', false)(state);

export const getTextAroundSerialNumber = state => {
  const defaultValue = '(,)';
  const value = String(getSetting('text_around_serial_number', '(,)')(state));
  const regex = /^(.{1},.{1})?$/;
  if (regex.test(value)) {
    return value;
  }
  return defaultValue;
};

export const getSerialNumberFormatterFn = createSelector(
  getTextAroundSerialNumber,
  wrapperAsString => {
    const [firstSymbol = '', lastSymbol = ''] = wrapperAsString.split(',');
    return serialNumber =>
      wrapperAsString
        ? `${firstSymbol}${serialNumber}${lastSymbol}`
        : serialNumber;
  },
);

/** @type Selector<Record<'inputs'|'mousemove'|'openCloseDay'|'stockLevels'|'zReport'|'customerSelected'|'productsInCart', boolean>> */
export const getPosTimeoutSettings = state =>
  R.tryCatch(
    JSON.parse,
    R.always({
      inputs: false,
      mousemove: false,
      openCloseDay: true,
      stockLevels: true,
      zReport: true,
      customerSelected: true,
      productsInCart: true,
      browsingSales: false,
      creatingCustomer: true,
    }),
  )(getSavedSetting('pos_timeout_config', 'error')(state));

export const getSavedPosTimeoutSettings = createSelector(
  getSavedSetting('pos_timeout_config', 'error'),
  R.tryCatch(
    JSON.parse,
    R.always({
      inputs: false,
      mousemove: false,
      openCloseDay: true,
      stockLevels: true,
      zReport: true,
      customerSelected: true,
      productsInCart: true,
    }),
  ),
);

export const getGiftcardSerialConfig = state =>
  getSetting('pos_serial_giftcard_pattern')(state);

export const getGiftcardSerialRegex = createSelector(
  getGiftcardSerialConfig,
  pattern => {
    if (!pattern) {
      // Source: https://datatracker.ietf.org/doc/html/rfc4122#page-14
      // v4 UUIDs are mostly random, except...
      // 1) the two most significant bits (bits 6 and 7) of clock_seq_hi_and_reserved are zero and one, respectively
      //    Translation: The first character of c_s_h_a_r must be 8, 9, a, or b
      // 2) the four most significant bits (bits 12 through 15) of time_hi_and_version to the 4-bit version number
      //    Translation: The first character of t_h_a_v must be 4
      //
      //                time-low  - xxxxxxxx
      //                time-mid  - xxxx
      //   time-high-and-version  - 4xxx
      //  clock-seq-and-reserved
      //         & clock-seq-low  - Hxxx  (H = 8|9|a|b)
      //                    node  - xxxxxxxxxxxx
      //
      //       time-low   time-mid    t-h-a-v   c-s-a-r & c-s-l     node
      //      |        | |        | |         | |              | |         |
      // return /^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/;
      return /.+/;
    }
    try {
      return new RegExp(`^${pattern}$`);
    } catch {
      return /.+/;
    }
  },
);

export const getCurrentPosLanguageLegacy = createSelector(
  getPosLanguages,
  getCurrentPosLanguage,
  getSetting('default_language'),
  (langs, currentLang, defaultLang) => {
    const exactLang = langs.find(
      l => currentLang === l?.isoCode || currentLang === l.code,
    );
    return (
      (exactLang ?? langs.find(l => currentLang.split('-')[0] === l?.isoCode))
        ?.legacyIdentifier ?? defaultLang
    );
  },
);

/**
 * Indicates if the logged-in user should see the employees form all locations
 * or only from the current location
 */
export const getHideEmployeesFromOtherLocation = getSetting(
  'touchpos_hide_employees_from_other_location',
  false,
);

export const getAllowFractionalProductQuantities = state =>
  getSetting('allow_fractional_product_quantities', true)(state);

export const getShoppingCartRowMergingCriteria = state =>
  getSetting('touchpos_group_items_in_row_by', 'consecutive')(state);

export const getUseReceiptTemplates = state =>
  getSetting('use_actual_reports_templates')(state);

export const getReceiptTemplateIdByDocType = (docType = '') => state =>
  getSetting(`actual_reports_template_for_${docType.toLowerCase()}`, 0)(state);

export function getNumberFormat(state) {
  const numberFormat = getSetting('numberformat', '. ')(state);
  let decimalSeparator;
  let thousandsSeparator;
  try {
    [decimalSeparator, thousandsSeparator] = numberFormat;
    if (
      typeof decimalSeparator !== 'string' ||
      typeof thousandsSeparator !== 'string'
    ) {
      return '. ';
    }
    return numberFormat;
  } catch (e) {
    return '. ';
  }
}

/**
 * Function that can be used to format ordinary digits like 12, 561563, etc to the account's `numberformat`
 */
export const getNumberFormatFunction = createSelector(
  state => getNumberFormat(state),
  ([decimalSep, thousandsSep]) => {
    /**
     * Formats a number according to the `numberformat` config
     * @param {number} num
     * @returns formatted number
     */
    const format = num => {
      /* de-DE = 23.456.789,123456123 */
      return num.toLocaleString('de-DE').replace(
        /[,.]/g,
        match =>
          ({
            '.': thousandsSep,
            ',': decimalSep,
          }[match]),
      );
    };
    return format;
  },
);
